import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { editions } from '../../data/editions';

const editionStyles = {
  micro: {
    color: '#7B2FBE',
    bg: 'linear-gradient(135deg, #F3EEFF 0%, #EDE7F6 100%)',
    border: 'rgba(123,47,190,0.2)',
    badgeBg: '#7B2FBE',
    btnColor: '#7B2FBE',
    checkColor: '#7B2FBE',
    glowColor: 'rgba(123,47,190,0.15)',
  },
  macro: {
    color: '#1565C0',
    bg: 'linear-gradient(135deg, #EFF6FF 0%, #E3F2FD 100%)',
    border: 'rgba(21,101,192,0.2)',
    badgeBg: '#1565C0',
    btnColor: '#1565C0',
    checkColor: '#1565C0',
    glowColor: 'rgba(21,101,192,0.15)',
  },
};

const EditionHighlights = () => {
  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {editions.map((edition, index) => {
          const style = editionStyles[edition.id] || editionStyles.micro;
          return (
            <motion.div
              key={edition.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              whileHover={{ y: -8 }}
              className="relative"
            >
              <div
                className="rounded-2xl p-7 md:p-9 h-full relative overflow-hidden"
                style={{
                  background: style.bg,
                  border: `1px solid ${style.border}`,
                  boxShadow: `0 8px 40px ${style.glowColor}`,
                }}
              >
                {/* Age Badge */}
                <div
                  className="absolute top-5 right-5 px-3 py-1.5 rounded-full font-display font-bold text-xs text-white"
                  style={{ background: style.badgeBg }}
                >
                  Ages {edition.ageRange}
                </div>

                {/* Decorative bg emoji */}
                <div
                  className="absolute bottom-2 right-4 text-8xl opacity-10 pointer-events-none select-none"
                  style={{ lineHeight: 1 }}
                >
                  {edition.id === 'micro' ? '🎨' : '🧩'}
                </div>

                {/* Content */}
                <div className="relative z-10">
                  <div className="text-6xl mb-4 text-center">
                    {edition.id === 'micro' ? '🌈' : '🚀'}
                  </div>

                  <h3
                    className="text-3xl font-display font-bold mb-3 text-center"
                    style={{ color: style.color }}
                  >
                    {edition.name}
                  </h3>

                  <p className="text-base text-neutral-gray-dark text-center mb-6 leading-relaxed">
                    {edition.description}
                  </p>

                  {/* Features */}
                  <div className="mb-7">
                    <h4
                      className="font-display font-bold text-sm mb-3 text-center uppercase tracking-wide"
                      style={{ color: style.color, opacity: 0.8 }}
                    >
                      What's Inside
                    </h4>
                    <ul className="space-y-2">
                      {edition.features.map((feature, idx) => (
                        <motion.li
                          key={idx}
                          initial={{ opacity: 0, x: -15 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.08 * idx }}
                          className="flex items-center gap-3 text-sm text-neutral-gray-dark"
                        >
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold"
                            style={{ background: style.checkColor }}
                          >
                            ✓
                          </div>
                          {feature}
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/samples"
                      className="flex-1 text-center py-3 px-4 rounded-button font-display font-semibold text-sm text-white transition-all hover:scale-105"
                      style={{ background: style.color, boxShadow: `0 4px 16px ${style.glowColor}` }}
                    >
                      Read Sample 📖
                    </Link>
                    <Link
                      to="/subscribe"
                      className="flex-1 text-center py-3 px-4 rounded-button font-display font-semibold text-sm transition-all hover:scale-105"
                      style={{ background: 'white', color: style.color, border: `1.5px solid ${style.border}` }}
                    >
                      Subscribe 🎉
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Bottom hint */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 }}
        className="mt-10 text-center"
      >
        <p className="text-neutral-gray-dark mb-2">Not sure which edition is right for your child?</p>
        <Link
          to="/contact"
          className="font-display font-bold text-lg transition-colors"
          style={{ color: '#3CB044' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#1E7C24'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#3CB044'; }}
        >
          Contact us for guidance →
        </Link>
      </motion.div>
    </div>
  );
};

export default EditionHighlights;